'use client';

import React, { useState, useEffect } from 'react';
import Icon from '@/components/ui/AppIcon';

interface QuoteResultProps {
  quote: {
    moveType: string;
    moveDate: string;
    fromLocation: string;
    toLocation: string;
    propertySize: string;
    packingService: boolean;
    assemblyService: boolean;
    storageService: boolean;
    specialItems: string[];
    contactName: string;
    contactPhone: string;
    contactEmail: string;
    estimatedPrice: number;
    distance: number;
  };
  onNewQuote: () => void;
}

const QuoteResult = ({ quote, onNewQuote }: QuoteResultProps) => {
  const [isHydrated, setIsHydrated] = useState(false);
  const [emailSent, setEmailSent] = useState(false);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  const handleSaveQuote = () => {
    if (!isHydrated) return;
    
    const quoteData = JSON.stringify(quote, null, 2);
    const blob = new Blob([quoteData], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `almanara-quote-${Date.now()}.json`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  const handleEmailQuote = () => {
    setEmailSent(true);
    setTimeout(() => setEmailSent(false), 3000);
  };

  const handleWhatsAppContact = () => {
    if (!isHydrated) return;
    
    const message = encodeURIComponent(
      `Hi Almanara Moving! I received a quote for ${quote.estimatedPrice} KWD. I'd like to discuss my move from ${quote.fromLocation} to ${quote.toLocation} on ${quote.moveDate}.`
    );
    window.open(`https://wa.me/96569911181?text=${message}`, '_blank');
  };

  const handlePhoneCall = () => {
    if (!isHydrated) return;
    window.location.href = 'tel:69911181';
  };

  if (!isHydrated) {
    return (
      <div className="bg-card rounded-xl shadow-soft p-8">
        <div className="animate-pulse space-y-6">
          <div className="h-12 bg-muted rounded w-3/4"></div>
          <div className="h-32 bg-muted rounded"></div>
          <div className="h-24 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  const priceBreakdown = [
    { label: 'Base Moving Service', value: 50 },
    { label: `Distance (${quote.distance} km)`, value: quote.distance * 2 },
    ...(quote.packingService ? [{ label: 'Professional Packing', value: 80 }] : []),
    ...(quote.assemblyService ? [{ label: 'Furniture Assembly', value: 60 }] : []),
    ...(quote.storageService ? [{ label: 'Temporary Storage', value: 100 }] : []),
    ...(quote.specialItems.length > 0
      ? [{ label: `Special Items (${quote.specialItems.length})`, value: quote.specialItems.length * 30 }]
      : []),
  ];

  return (
    <div className="space-y-6">
      {/* Success Header */}
      <div className="bg-gradient-to-r from-success to-brand-trust rounded-xl p-8 text-center">
        <div className="flex justify-center mb-4">
          <div className="w-16 h-16 bg-white rounded-full flex items-center justify-center">
            <Icon name="CheckCircleIcon" size={40} variant="solid" className="text-success" />
          </div>
        </div>
        <h2 className="text-3xl font-headline font-bold text-white mb-2">
          Your Quote is Ready!
        </h2>
        <p className="text-white text-opacity-90 font-body">
          We&apos;ve calculated your personalized moving estimate
        </p>
      </div>

      {/* Price Display */}
      <div className="bg-card rounded-xl shadow-soft p-8 text-center">
        <p className="text-text-secondary font-body mb-2">Estimated Total Cost</p>
        <div className="flex items-center justify-center space-x-2 mb-4">
          <span className="text-5xl font-headline font-bold text-primary">
            {quote.estimatedPrice}
          </span>
          <span className="text-2xl font-body text-text-secondary">KWD</span>
        </div>
        <div className="flex items-center justify-center space-x-2 text-text-secondary">
          <Icon name="InformationCircleIcon" size={16} variant="outline" />
          <p className="text-sm font-body">
            Final price may vary based on actual inventory and conditions
          </p>
        </div>
      </div>

      {/* Move Details Summary */}
      <div className="bg-card rounded-xl shadow-soft p-6">
        <h3 className="text-xl font-headline font-bold text-text-primary mb-4">
          Move Details
        </h3>
        <div className="space-y-4">
          <div className="flex items-start space-x-3">
            <Icon name="CalendarIcon" size={20} variant="outline" className="text-primary mt-0.5" />
            <div>
              <p className="text-sm font-body text-text-secondary">Move Date</p>
              <p className="font-body font-semibold text-text-primary">{quote.moveDate}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="MapPinIcon" size={20} variant="outline" className="text-primary mt-0.5" />
            <div>
              <p className="text-sm font-body text-text-secondary">From</p>
              <p className="font-body font-semibold text-text-primary">{quote.fromLocation}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="MapPinIcon" size={20} variant="solid" className="text-primary mt-0.5" />
            <div>
              <p className="text-sm font-body text-text-secondary">To</p>
              <p className="font-body font-semibold text-text-primary">{quote.toLocation}</p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="HomeIcon" size={20} variant="outline" className="text-primary mt-0.5" />
            <div>
              <p className="text-sm font-body text-text-secondary">Property Size</p>
              <p className="font-body font-semibold text-text-primary capitalize">
                {quote.propertySize.replace(/([A-Z])/g, ' $1').trim()}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Price Breakdown */}
      <div className="bg-card rounded-xl shadow-soft p-6">
        <h3 className="text-xl font-headline font-bold text-text-primary mb-4">
          Price Breakdown
        </h3>
        <div className="space-y-3">
          {priceBreakdown.map((item, index) => (
            <div key={index} className="flex items-center justify-between py-2 border-b border-border last:border-0">
              <span className="font-body text-text-primary">{item.label}</span>
              <span className="font-body font-semibold text-text-primary">{item.value} KWD</span>
            </div>
          ))}
          <div className="flex items-center justify-between py-3 border-t-2 border-primary">
            <span className="font-headline font-bold text-text-primary text-lg">Total</span>
            <span className="font-headline font-bold text-primary text-lg">
              {quote.estimatedPrice} KWD
            </span>
          </div>
        </div>
      </div>

      {/* Contact Actions */}
      <div className="bg-gradient-to-br from-primary to-secondary rounded-xl p-6 text-white">
        <h3 className="text-xl font-headline font-bold mb-2">Ready to Book?</h3>
        <p className="font-body mb-6 text-white text-opacity-90">
          Contact us now to confirm your move and secure your preferred date
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <button
            onClick={handlePhoneCall}
            className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg bg-white text-primary hover:bg-opacity-90 transition-all duration-base font-body font-semibold"
          >
            <Icon name="PhoneIcon" size={20} variant="solid" />
            <span>Call 6991 1181</span>
          </button>
          <button
            onClick={handleWhatsAppContact}
            className="flex items-center justify-center space-x-2 px-6 py-3 rounded-lg bg-success text-white hover:bg-opacity-90 transition-all duration-base font-body font-semibold"
          >
            <Icon name="ChatBubbleLeftRightIcon" size={20} variant="solid" />
            <span>WhatsApp Us</span>
          </button>
        </div>
      </div>

      {/* Quote Actions */}
      <div className="flex flex-col sm:flex-row gap-3">
        <button
          onClick={handleEmailQuote}
          disabled={emailSent}
          className={`flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg border-2 border-primary text-primary hover:bg-primary hover:text-white transition-all duration-base font-body font-semibold ${
            emailSent ? 'bg-success border-success text-white' : ''
          }`}
        >
          <Icon
            name={emailSent ? 'CheckCircleIcon' : 'EnvelopeIcon'}
            size={20}
            variant={emailSent ? 'solid' : 'outline'}
          />
          <span>{emailSent ? 'Email Sent!' : 'Email Quote'}</span>
        </button>
        <button
          onClick={handleSaveQuote}
          className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg border-2 border-border hover:border-primary hover:bg-muted transition-all duration-base font-body font-semibold text-text-primary"
        >
          <Icon name="ArrowDownTrayIcon" size={20} variant="outline" />
          <span>Save Quote</span>
        </button>
        <button
          onClick={onNewQuote}
          className="flex-1 flex items-center justify-center space-x-2 px-6 py-3 rounded-lg bg-muted hover:bg-border transition-all duration-base font-body font-semibold text-text-primary"
        >
          <Icon name="ArrowPathIcon" size={20} variant="outline" />
          <span>New Quote</span>
        </button>
      </div>

      {/* Trust Indicators */}
      <div className="bg-muted rounded-xl p-6">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="flex items-start space-x-3">
            <Icon name="ShieldCheckIcon" size={24} variant="solid" className="text-success" />
            <div>
              <p className="font-body font-semibold text-text-primary">Fully Insured</p>
              <p className="text-sm text-text-secondary font-body">
                Your belongings are protected
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="ClockIcon" size={24} variant="solid" className="text-primary" />
            <div>
              <p className="font-body font-semibold text-text-primary">24/7 Available</p>
              <p className="text-sm text-text-secondary font-body">
                We work around your schedule
              </p>
            </div>
          </div>
          <div className="flex items-start space-x-3">
            <Icon name="StarIcon" size={24} variant="solid" className="text-accent" />
            <div>
              <p className="font-body font-semibold text-text-primary">5-Star Service</p>
              <p className="text-sm text-text-secondary font-body">
                Trusted by 500+ customers
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuoteResult;